import { useMutation, useQuery } from '@tanstack/react-query';
import { Plus, Tag } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { trpc } from '@/utils/trpc';

interface CategoryCreatorProps {
  compact?: boolean;
}

const CATEGORY_ICONS = [
  '🍽️',
  '🚗',
  '🛍️',
  '🎬',
  '💡',
  '🏥',
  '📚',
  '💅',
  '🏠',
  '📦',
  '💰',
  '💻',
  '📈',
  '🎁',
  '💵',
  '📊',
  '₿',
  '🏢',
  '🎯',
  '📱',
];

const CATEGORY_COLORS = [
  '#FF6B6B',
  '#4ECDC4',
  '#95E77E',
  '#FFE66D',
  '#6C5CE7',
  '#00B894',
  '#FDCB6E',
  '#FD79A8',
  '#A29BFE',
  '#B2BEC3',
];

interface CategoryFormData {
  name: string;
  type: 'expense' | 'income' | 'transfer' | 'investment';
  icon: string;
  color: string;
}

export function CategoryCreator({ compact = false }: CategoryCreatorProps) {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [formData, setFormData] = useState<CategoryFormData>({
    name: '',
    type: 'expense',
    icon: '📦',
    color: '#B2BEC3',
  });

  // Queries
  const categories = useQuery(trpc.categories.list.queryOptions({}));

  // Mutations
  const createCategory = useMutation(
    trpc.categories.create.mutationOptions({
      onSuccess: () => {
        categories.refetch();
        setIsCreateDialogOpen(false);
        resetForm();
      },
    })
  );

  const createDefaults = useMutation(
    trpc.categories.createDefaults.mutationOptions({
      onSuccess: () => {
        categories.refetch();
      },
    })
  );

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'expense',
      icon: '📦',
      color: '#B2BEC3',
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createCategory.mutate(formData);
  };

  const categoriesData = categories.data || [];

  // Compact version - just a button with a dialog
  if (compact) {
    return (
      <Dialog onOpenChange={setIsCreateDialogOpen} open={isCreateDialogOpen}>
        <DialogTrigger asChild>
          <Button className="gap-2" variant="outline">
            <Plus className="h-4 w-4" />
            Nova Categoria
          </Button>
        </DialogTrigger>
        <DialogContent>
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>Criar Nova Categoria</DialogTitle>
              <DialogDescription>
                Adicione uma nova categoria para seus gastos ou receitas
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="name">Nome</Label>
                <Input
                  id="name"
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="Ex: Alimentação"
                  value={formData.name}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="type">Tipo</Label>
                <Select
                  onValueChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      type: value as CategoryFormData['type'],
                    }))
                  }
                  value={formData.type}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="expense">Gasto</SelectItem>
                    <SelectItem value="income">Receita</SelectItem>
                    <SelectItem value="transfer">Transferência</SelectItem>
                    <SelectItem value="investment">Investimento</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label>Ícone</Label>
                <div className="grid grid-cols-10 gap-2">
                  {CATEGORY_ICONS.map((icon) => (
                    <button
                      className={`rounded-md border p-2 text-lg transition-colors hover:bg-muted ${
                        formData.icon === icon
                          ? 'bg-primary text-primary-foreground'
                          : ''
                      }`}
                      key={icon}
                      onClick={() => setFormData((prev) => ({ ...prev, icon }))}
                      type="button"
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid gap-2">
                <Label>Cor</Label>
                <div className="grid grid-cols-10 gap-2">
                  {CATEGORY_COLORS.map((color) => (
                    <button
                      className={`h-8 w-8 rounded-full border-2 transition-all ${
                        formData.color === color
                          ? 'scale-110 border-foreground'
                          : 'border-muted'
                      }`}
                      key={color}
                      onClick={() =>
                        setFormData((prev) => ({ ...prev, color }))
                      }
                      style={{ backgroundColor: color }}
                      type="button"
                    />
                  ))}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button
                onClick={() => {
                  setIsCreateDialogOpen(false);
                  resetForm();
                }}
                type="button"
                variant="outline"
              >
                Cancelar
              </Button>
              <Button
                disabled={createCategory.isPending || !formData.name.trim()}
                type="submit"
              >
                Criar Categoria
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    );
  }

  // Full version - with card and category listing
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5" />
              Gerenciar Categorias
            </CardTitle>
            <CardDescription>
              Crie e gerencie suas categorias de transações
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {categoriesData.length === 0 && (
              <Button
                disabled={createDefaults.isPending}
                onClick={() => createDefaults.mutate()}
                size="sm"
                variant="outline"
              >
                Criar Padrões
              </Button>
            )}
            <Dialog
              onOpenChange={setIsCreateDialogOpen}
              open={isCreateDialogOpen}
            >
              <DialogTrigger asChild>
                <Button className="gap-2" size="sm">
                  <Plus className="h-4 w-4" />
                  Nova Categoria
                </Button>
              </DialogTrigger>
              <DialogContent>
                <form onSubmit={handleSubmit}>
                  <DialogHeader>
                    <DialogTitle>Criar Nova Categoria</DialogTitle>
                    <DialogDescription>
                      Adicione uma nova categoria para organizar suas transações
                    </DialogDescription>
                  </DialogHeader>

                  <div className="grid gap-4 py-4">
                    <div className="grid gap-2">
                      <Label htmlFor="name">Nome da Categoria</Label>
                      <Input
                        id="name"
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                        placeholder="Ex: Alimentação, Transporte..."
                        required
                        value={formData.name}
                      />
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="type">Tipo</Label>
                      <Select
                        onValueChange={(
                          value:
                            | 'expense'
                            | 'income'
                            | 'transfer'
                            | 'investment'
                        ) => setFormData((prev) => ({ ...prev, type: value }))}
                        value={formData.type}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="expense">Gasto</SelectItem>
                          <SelectItem value="income">Receita</SelectItem>
                          <SelectItem value="transfer">
                            Transferência
                          </SelectItem>
                          <SelectItem value="investment">
                            Investimento
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid gap-2">
                      <Label>Ícone</Label>
                      <div className="grid grid-cols-10 gap-2">
                        {CATEGORY_ICONS.map((icon) => (
                          <button
                            className={`rounded-md border p-2 text-lg transition-colors hover:bg-muted ${
                              formData.icon === icon
                                ? 'bg-primary text-primary-foreground'
                                : ''
                            }`}
                            key={icon}
                            onClick={() =>
                              setFormData((prev) => ({ ...prev, icon }))
                            }
                            type="button"
                          >
                            {icon}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="grid gap-2">
                      <Label>Cor</Label>
                      <div className="grid grid-cols-10 gap-2">
                        {CATEGORY_COLORS.map((color) => (
                          <button
                            className={`h-8 w-8 rounded-full border-2 transition-all ${
                              formData.color === color
                                ? 'scale-110 border-foreground'
                                : 'border-muted'
                            }`}
                            key={color}
                            onClick={() =>
                              setFormData((prev) => ({ ...prev, color }))
                            }
                            style={{ backgroundColor: color }}
                            type="button"
                          />
                        ))}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 rounded-md bg-muted p-3">
                      <span className="text-2xl">{formData.icon}</span>
                      <div
                        className="h-4 w-4 rounded-full"
                        style={{ backgroundColor: formData.color }}
                      />
                      <span className="font-medium">
                        {formData.name || 'Nome da categoria'}
                      </span>
                    </div>
                  </div>

                  <DialogFooter>
                    <Button
                      onClick={() => {
                        setIsCreateDialogOpen(false);
                        resetForm();
                      }}
                      type="button"
                      variant="outline"
                    >
                      Cancelar
                    </Button>
                    <Button
                      disabled={
                        createCategory.isPending || !formData.name.trim()
                      }
                      type="submit"
                    >
                      Criar Categoria
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {categories.isPending ? (
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => (
              <div className="h-12 animate-pulse rounded bg-muted" key={i} />
            ))}
          </div>
        ) : categoriesData.length === 0 ? (
          <div className="py-8 text-center">
            <Tag className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
            <h3 className="mb-2 font-semibold">Nenhuma categoria encontrada</h3>
            <p className="mb-4 text-muted-foreground text-sm">
              Crie suas primeiras categorias ou use as categorias padrão
            </p>
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {categoriesData.map((category) => (
              <div
                className="flex items-center gap-3 rounded-lg border p-3"
                key={category.id}
              >
                <span className="text-xl">{category.icon}</span>
                <div
                  className="h-3 w-3 rounded-full"
                  style={{ backgroundColor: category.color ?? '#B2BEC3' }}
                />
                <div className="flex-1">
                  <p className="font-medium">{category.name}</p>
                  <p className="text-muted-foreground text-xs">
                    {category.type === 'expense'
                      ? 'Gasto'
                      : category.type === 'income'
                        ? 'Receita'
                        : category.type === 'transfer'
                          ? 'Transferência'
                          : 'Investimento'}
                    {category.transactionCount > 0 && (
                      <span> • {category.transactionCount} transações</span>
                    )}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
